import { Scale } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useEffect, useState } from 'react'
import { styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import { IInstrumentStemStore } from '../../../../stores/song/stem/InstrumentStemStore'
import KeyScaleManager, { KeyAndScale } from './KeyScaleManager'
import { getKeyType, isVisible, KEYBOARD_WIDTH, NOTE_COUNT, NOTE_LABELS } from './PianoRollUtils'

const KeyContainer = styled('div', {
  borderTopRightRadius: '2px',
  borderBottomRightRadius: '2px',
  fontSize: '10px',
  lineHeight: '10px',
  textAlign: 'right',
  paddingRight: '4px',
  boxSizing: 'border-box',
  transition: 'background 50ms ease',
  fontFamily: '$dm',
  variants: {
    type: {
      black: {
        width: '32px',
        height: '16px',
        background: '$gray14',
        color: '$gray05',
        float: 'left',
        transform: 'translateY(-10px)',
        '&:hover': {
          background: '$gray13'
        },
        '&:active': {
          background: '$gray12'
        }
      },
      nonChromatic: {
        background: '$gray01',
        margin: '2px 0px',
        fontSize: '9px',
        lineHeight: '14px',
        height: '14px',
        width: '64px',
        color: 'rgba(0,0,0,.3)',
        '&:hover': {
          background: '$gray02'
        },
        '&:active': {
          background: '$gray03'
        }
      },
      big: {
        background: '$gray01',
        margin: '2px 0px',
        height: '30px',
        width: '64px',
        color: 'rgba(0,0,0,.3)',
        '&:hover': {
          background: '$gray02'
        },
        '&:active': {
          background: '$gray03'
        }
      },
      small: {
        background: '$gray01',
        margin: '2px 0px',
        height: '22px',
        width: '64px',
        paddingTop: '10px',
        color: 'rgba(0,0,0,.3)',
        '&:hover': {
          background: '$gray02'
        },
        '&:active': {
          background: '$gray03'
        }
      }
    }
  }
})

const KeyboardContainer = styled('div', {
  position: 'absolute',
  width: KEYBOARD_WIDTH,
  margin: '-1px 0px',
  overflow: 'visible',
  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))'
})

interface KeyboardOptions {}

type KeyboardProps = KeyboardOptions & ComponentProps<typeof KeyboardContainer>

const keys = new Array(NOTE_COUNT).fill(true).map((_, i) => {
  const index = NOTE_COUNT - 1 - i
  return { index, type: getKeyType(index) }
})

const filter = ({ scale, key }: KeyAndScale) =>
  keys.filter(({ index }) => isVisible(index, scale, key))

const Keyboard: FC<KeyboardProps> = ({ ...props }) => {
  const { studio } = useTreeState()
  const { selectedStemIndex, currentSection, song } = studio
  const stem = song.stems[selectedStemIndex] as IInstrumentStemStore
  const { melodicSample } = stem
  const handleClick = (note: number) => () =>
    studio.playSampleWithEffects(melodicSample, stem.getStemGain(), note)
  const [{ key, scale }, setState] = useState(KeyScaleManager.state)
  useEffect(() => {
    KeyScaleManager.subscribe('keyboard', (state) => setState(state), false)
    return () => KeyScaleManager.unsubscribe('keyboard')
  }, [])
  const filtered = filter({ scale, key })
  return (
    <KeyboardContainer>
      {filtered.map(({ index, type }, i) => (
        <KeyContainer
          key={index}
          type={scale === Scale.CHROMATIC ? type : 'nonChromatic'}
          onPointerDown={handleClick(index)}
        >
          {(index % 12 === 0 || scale !== Scale.CHROMATIC) &&
            `${NOTE_LABELS[index % 12]}${Math.floor(index / 12)}`}
        </KeyContainer>
      ))}
    </KeyboardContainer>
  )
}

export default observer(Keyboard)
